/**
 * 蜜糖黄 · 阳光黄页 分类列表页
 *
 * 目录黄页感：筛选胶囊 + 横向「条目卡」列表（左方图右文字），置顶条目黑色角标置前。
 * 数据：getListings({ category_id, q, page, page_size })；挂载即加载，无 useSite 守卫。
 */

import { useState, useEffect, useMemo, useCallback } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { getListings, type ListingItem } from "@/utils/api";
import { useSite } from "@/context/SiteContext";
import { useSiteConfig } from "@/context/SiteConfigContext";
import HoneyNavbar from "@/templates/honey/components/Navbar";
import HoneyFooter from "@/templates/honey/components/Footer";

function formatTimeAgo(dateStr?: string) {
  if (!dateStr) return "";
  const diff = Date.now() - new Date(dateStr).getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return "刚刚";
  if (minutes < 60) return `${minutes} 分钟前`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} 小时前`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days} 天前`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months} 个月前`;
  return `${Math.floor(months / 12)} 年前`;
}

function formatViewCount(count?: number) {
  if (!count) return "0";
  if (count >= 10000) return `${(count / 10000).toFixed(1)}w`;
  return String(count);
}

export default function HoneyCategoryPage() {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { siteName } = useSite();
  const { navCategories } = useSiteConfig();

  const [activeCategoryId, setActiveCategoryId] = useState<number | undefined>(undefined);
  const [keyword, setKeyword] = useState("");
  const [list, setList] = useState<ListingItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [loading, setLoading] = useState(true);

  const urlKeyword = searchParams.get("keyword") || searchParams.get("q") || "";

  // 从 URL 关键字 + slug 重置筛选
  useEffect(() => {
    setKeyword(urlKeyword);
  }, [urlKeyword]);

  useEffect(() => {
    setPage(1);
    if (slug) {
      const found = navCategories.find((c: any) => c.slug === slug);
      setActiveCategoryId(found ? found.id : undefined);
    } else {
      setActiveCategoryId(undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, urlKeyword, navCategories]);

  // 挂载即加载（含筛选变化）
  const loadList = useCallback(() => {
    setLoading(true);
    const params: any = { page, page_size: pageSize };
    if (activeCategoryId) params.category_id = activeCategoryId;
    if (keyword.trim()) params.q = keyword.trim();

    getListings(params)
      .then((res) => {
        setList(Array.isArray(res.list) ? res.list : []);
        setTotal(typeof res.total === "number" ? res.total : 0);
      })
      .catch(() => {
        setList([]);
        setTotal(0);
      })
      .finally(() => setLoading(false));
  }, [activeCategoryId, keyword, page, pageSize]);

  useEffect(() => {
    loadList();
  }, [loadList]);

  // 置顶优先展示
  const sortedList = useMemo(
    () =>
      [...list].sort((a, b) => Number(Boolean(b.is_pinned)) - Number(Boolean(a.is_pinned))),
    [list]
  );

  const activeCategory = navCategories.find((c: any) => c.id === activeCategoryId);
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const changeCategory = (cat: any) => {
    if (cat) navigate(`/category/${cat.slug}`);
    else navigate("/category");
  };

  const jumpPage = (p: number) => {
    if (p < 1 || p > totalPages || p === page) return;
    setPage(p);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="min-h-screen bg-[#FFF9EC]">
      <HoneyNavbar />
      <div className="lg:pl-60">
        {/* 页头（琥珀目录角标） */}
        <section className="bg-gradient-to-r from-[#FFF3D6] via-[#FFF9EC] to-[#FFF3D6] border-b border-[#EAE1CB]">
          <div className="max-w-7xl mx-auto px-4 lg:px-8 py-8">
            <nav className="flex items-center gap-1.5 text-xs text-[#8B8577]">
              <Link to="/" className="hover:text-[#B87A00] transition-colors">首页</Link>
              <i className="ri-arrow-right-s-line" />
              <span className="text-[#6E6658]">{activeCategory?.name || (keyword ? `搜索：${keyword}` : "全部分类")}</span>
            </nav>
            <div className="mt-4 flex items-center gap-4">
              <span className="w-12 h-12 rounded-2xl bg-[#23201A] text-[#F0A500] flex items-center justify-center shrink-0">
                <i className={`${activeCategory?.icon || "ri-folder-open-line"} text-2xl`} />
              </span>
              <div>
                <h1 className="text-2xl font-black text-[#23201A] tracking-tight">
                  {keyword ? `「${keyword}」的搜索结果` : (activeCategory?.name || `欢迎来到 ${siteName || "信息分类"}`)}
                </h1>
                <p className="mt-1 text-xs text-[#8B8577]">
                  共 {total} 条信息
                  {keyword && (
                    <button onClick={() => navigate("/category")} className="ml-2 text-[#B87A00] hover:underline">
                      清除关键字
                    </button>
                  )}
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* 主区域 */}
        <section className="max-w-7xl mx-auto px-4 lg:px-8 py-8">
          {/* 分类筛选胶囊 */}
          <div className="flex items-center gap-2 overflow-x-auto pb-1 mb-7 no-scrollbar">
            <button
              onClick={() => changeCategory(null)}
              className={`shrink-0 inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-[13px] font-bold transition-colors cursor-pointer ${
                !activeCategoryId && !keyword
                  ? "bg-[#23201A] text-[#F0A500]"
                  : "bg-white border border-[#EAE1CB] text-[#6E6658] hover:border-[#F0A500] hover:text-[#B87A00]"
              }`}
            >
              <i className="ri-apps-2-line" />全部
            </button>
            {navCategories.map((cat: any) => (
              <button
                key={cat.id}
                onClick={() => changeCategory(cat)}
                className={`shrink-0 inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-[13px] font-bold transition-colors cursor-pointer ${
                  activeCategoryId === cat.id
                    ? "bg-[#23201A] text-[#F0A500]"
                    : "bg-white border border-[#EAE1CB] text-[#6E6658] hover:border-[#F0A500] hover:text-[#B87A00]"
                }`}
              >
                <i className={`${cat.icon || "ri-folder-line"}`} />
                {cat.name}
              </button>
            ))}
          </div>

          {/* 列表加载态 */}
          {loading ? (
            <div className="space-y-3">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="bg-white border border-[#EAE1CB] rounded-xl p-4 flex gap-4 animate-pulse">
                  <div className="w-24 h-24 sm:w-28 sm:h-28 rounded-xl bg-[#F3ECDC] shrink-0" />
                  <div className="flex-1 py-1 space-y-2.5">
                    <div className="h-4 w-2/3 rounded bg-[#F3ECDC]" />
                    <div className="h-3 w-full rounded bg-[#F3ECDC]" />
                    <div className="h-3 w-4/5 rounded bg-[#F3ECDC]" />
                  </div>
                </div>
              ))}
            </div>
          ) : sortedList.length > 0 ? (
            <div className="space-y-3">
              {sortedList.map((item) => {
                const cover = Array.isArray(item.images) && item.images.length > 0 ? item.images[0] : "";
                return (
                  <Link
                    key={item.id}
                    to={`/listing/${item.id}`}
                    className="group flex items-center gap-4 sm:gap-5 bg-white border border-[#EAE1CB] rounded-xl p-3 sm:p-4 hover:border-[#F0A500] hover:shadow-lg hover:shadow-[#F0A500]/10 hover:-translate-y-0.5 transition-all"
                  >
                    {/* 左：方图 */}
                    <div className="relative w-24 h-24 sm:w-28 sm:h-28 rounded-xl overflow-hidden shrink-0 bg-[#FFF7E0]">
                      {cover ? (
                        <img src={cover} alt={item.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" loading="lazy" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <i className="ri-image-line text-3xl text-[#E9D9AE]" />
                        </div>
                      )}
                      {item.is_pinned && (
                        <span className="absolute top-1.5 left-1.5 inline-flex items-center gap-1 bg-[#23201A] text-[#F0A500] text-[10px] font-bold px-1.5 py-0.5 rounded-md">
                          <i className="ri-pushpin-2-fill" />置顶
                        </span>
                      )}
                    </div>

                    {/* 右：文字 */}
                    <div className="flex-1 min-w-0 py-1">
                      <h2 className="text-[15px] sm:text-base font-bold text-[#23201A] line-clamp-1 group-hover:text-[#B87A00] transition-colors">
                        {item.title}
                      </h2>
                      <p className="mt-1.5 text-xs text-[#6E6658] line-clamp-2 leading-5">
                        {typeof item.content === "string" ? item.content : ""}
                      </p>
                      <div className="mt-2.5 flex flex-wrap items-center gap-x-4 gap-y-1.5">
                        <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-[#B87A00] bg-[#FFF3D6] rounded-full px-2 py-0.5">
                          <i className="ri-folder-line" />
                          {item.category?.name || "分类信息"}
                        </span>
                        <span className="inline-flex items-center gap-1 text-[11px] text-[#8B8577]">
                          <i className="ri-eye-line" />{formatViewCount(item.view_count)} 次浏览
                        </span>
                        <span className="text-[11px] text-[#8B8577]">{formatTimeAgo(item.created_at)}</span>
                      </div>
                    </div>

                    {/* 右端箭头 */}
                    <i className="hidden sm:block text-lg text-[#E4D9BB] group-hover:text-[#F0A500] transition-colors shrink-0" />
                  </Link>
                );
              })}
            </div>
          ) : (
            <div className="bg-white border border-[#EAE1CB] rounded-xl py-20 text-center">
              <i className="ri-inbox-line text-5xl text-[#E4D9BB]" />
              <p className="mt-4 text-sm text-[#8B8577]">
                {keyword ? `没有找到与「${keyword}」相关的信息` : "该分类下暂无信息"}
              </p>
              <Link
                to="/publish"
                className="inline-flex items-center gap-2 mt-5 px-6 py-2.5 rounded-xl bg-[#F0A500] text-[#23201A] text-sm font-bold hover:bg-[#FFB520] transition-colors"
              >
                <i className="ri-add-circle-line" />立即发布
              </Link>
            </div>
          )}

          {/* 分页 */}
          {!loading && sortedList.length > 0 && totalPages > 1 && (
            <div className="mt-8 flex items-center justify-center gap-1.5">
              <button
                onClick={() => jumpPage(page - 1)}
                disabled={page === 1}
                className="w-9 h-9 rounded-lg bg-white border border-[#EAE1CB] text-[#6E6658] flex items-center justify-center hover:border-[#F0A500] hover:text-[#B87A00] disabled:opacity-40 disabled:hover:border-[#EAE1CB] disabled:hover:text-[#6E6658] disabled:cursor-not-allowed transition-colors cursor-pointer"
                aria-label="上一页"
              >
                <i className="ri-arrow-left-s-line text-lg" />
              </button>

              {Array.from({ length: totalPages }).map((_, i) => {
                const p = i + 1;
                if (totalPages > 7 && p > 2 && p < totalPages - 1 && Math.abs(p - page) > 2) {
                  if (p === 3 || p === totalPages - 2) {
                    return <span key={p} className="text-xs text-[#8B8577] px-1">…</span>;
                  }
                  return null;
                }
                return (
                  <button
                    key={p}
                    onClick={() => jumpPage(p)}
                    className={`min-w-9 h-9 px-2 rounded-lg text-[13px] font-bold transition-colors cursor-pointer ${
                      p === page
                        ? "bg-[#23201A] text-[#F0A500]"
                        : "bg-white border border-[#EAE1CB] text-[#6E6658] hover:border-[#F0A500] hover:text-[#B87A00]"
                    }`}
                  >
                    {p}
                  </button>
                );
              })}

              <button
                onClick={() => jumpPage(page + 1)}
                disabled={page === totalPages}
                className="w-9 h-9 rounded-lg bg-white border border-[#EAE1CB] text-[#6E6658] flex items-center justify-center hover:border-[#F0A500] hover:text-[#B87A00] disabled:opacity-40 disabled:hover:border-[#EAE1CB] disabled:hover:text-[#6E6658] disabled:cursor-not-allowed transition-colors cursor-pointer"
                aria-label="下一页"
              >
                <i className="ri-arrow-right-s-line text-lg" />
              </button>
            </div>
          )}
        </section>

        <HoneyFooter />
      </div>
    </div>
  );
}
